import { useMemo } from "react";

import { useAuthorization } from "~/hooks/useAuthorization";

import type { InterviewGuideDisplay, UserRef } from "../types";
import {
  ASSIGN_ROLES,
  HireRole,
  MANAGER_OR_HM_ROLES,
  MANAGER_ROLES,
  VIEW_GUIDE_ROLES,
} from "./roles";

/** Minimal guide shape needed for the per-guide editability check. */
type EditableGuide = Pick<InterviewGuideDisplay, "status"> & {
  inputRequestedFrom?: UserRef[];
};

export interface InterviewGuidePermissions {
  /** Resolved Hire role for the active org (undefined if the user has none). */
  role?: string;
  /**
   * True when the caller has no interview-manager access — Hire role `viewer`
   * (Reviewer) or no Hire role. Reviewers get the restricted interview view:
   * never the overall/aggregate score or other interviewers' scores. Panel
   * membership (checked per-interview, not here) further gates transcript / AI.
   */
  isReviewer: boolean;
  /** View the guides list + open a guide (everyone except Reviewer). */
  canViewGuides: boolean;
  /** Start the create-guide wizard (managers only). */
  canCreateGuide: boolean;
  /**
   * Manage a guide's structure + lifecycle: details, pre-interview guidance,
   * competencies, lock/unlock, request-input, move-to-draft, archive/restore,
   * delete, duplicate, publish-from-Draft (managers only — a Hiring Manager
   * never gets these, even when invited).
   */
  canManageGuides: boolean;
  /** View the question bank (everyone except Reviewer). */
  canViewBank: boolean;
  /** Edit / delete a shared bank question (managers only). */
  canManageBank: boolean;
  /** Add a question to the shared bank (managers + Hiring Manager). */
  canAddBankQuestion: boolean;
  /** Attach / detach a guide on an interview (everyone except Reviewer). */
  canAssignGuide: boolean;
  /**
   * Per-guide authority for the "invited Hiring Manager" actions: add / edit /
   * reorder / remove questions, save-to-bank, AI modify, and the batch save /
   * publish. Managers: always true. Hiring Manager: true when they were invited
   * (present in `inputRequestedFrom`) — in ANY status, since requesting input no
   * longer changes the guide's status. Everyone else: false.
   *
   * Mirrors the backend `canEditGuide` shield rule. FE checks are UX only — the
   * calendar subgraph enforces the same on every mutation.
   */
  canEditGuide: (guide: EditableGuide) => boolean;
}

/**
 * Resolve the current user's Interview Guides capabilities from their Hire role,
 * mirroring the calendar subgraph's permission rules. Read once per render.
 */
export function useInterviewGuidePermissions(): InterviewGuidePermissions {
  const { user, userId, orgId } = useAuthorization();
  const role = user?.accesses?.find((a) => a.organisationId === orgId)?.role;

  return useMemo(() => {
    const has = (set: readonly string[]) => !!role && set.includes(role);
    const isManager = has(MANAGER_ROLES);
    const isHiringManager = role === HireRole.hiringManager;

    const canEditGuide = (guide: EditableGuide): boolean => {
      if (isManager) return true;
      if (isHiringManager) {
        // Invited reviewers may edit in ANY status (the invite is the gate, not
        // the lifecycle). `inputRequestedFrom` is UserRef[] on the FE (objects)
        // — compare on `.id`. Do NOT switch to `.includes(userId)` (that is the
        // backend's raw string[] shape); it would always be false here and
        // silently lock out invited Hiring Managers.
        return (guide.inputRequestedFrom ?? []).some((u) => u.id === userId);
      }
      return false;
    };

    return {
      role,
      isReviewer: !has(ASSIGN_ROLES),
      canViewGuides: has(VIEW_GUIDE_ROLES),
      canCreateGuide: isManager,
      canManageGuides: isManager,
      canViewBank: has(VIEW_GUIDE_ROLES),
      canManageBank: isManager,
      canAddBankQuestion: has(MANAGER_OR_HM_ROLES),
      canAssignGuide: has(ASSIGN_ROLES),
      canEditGuide,
    };
  }, [role, userId]);
}
